import _ from 'lodash';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from 'react';
import {
  Grid,
  TextField,
  FormHelperText,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useUser } from '@/providers';

import { fetchProjectGpuClasses } from '@/actions/gpuClasses';
import GpuCountField from './GpuCountField';
import GpuTypeField from './GpuTypeField';
import JobNameField from './JobNameField';
import JOB_TYPES from '@/components/jobs/types';

const cpu_gpu_type_id = '78201a90-ca48-4147-b6f5-fec51b4bd7db';
const llm_disk_size_gb = 1;

export default function RequiredFields({
  initialValues,
  type,
  onChange,
  edit = false,
  small = false,
  disk_min = 10,
  disk_max = 1000,
}) {
  const dispatch = useDispatch();
  const user = useUser();
  const gpu_classes = useSelector((state) => state.gpuClasses || []);
  const is_fixed_disk_job = [
    JOB_TYPES.LLM.value,
    JOB_TYPES.GUARDRAIL.value,
  ].includes(type);
  const is_guardrail_job = type === JOB_TYPES.GUARDRAIL.value;
  const effective_disk_min = is_fixed_disk_job ? llm_disk_size_gb : disk_min;

  const normalize_resource_values = useCallback(
    (resource_values) => {
      const normalized = { ...resource_values };
      if (is_fixed_disk_job) {
        normalized.disk_size = llm_disk_size_gb;
      }
      if (is_guardrail_job) {
        normalized.gpu_types = [cpu_gpu_type_id];
        normalized.gpu_count = 0;
        normalized.max_price = 10;
      }
      return normalized;
    },
    [is_fixed_disk_job, is_guardrail_job],
  );

  const initial_values = initialValues || {
    job_name: '',
    gpu_types: [],
    gpu_count: 1,
    cpu_count: 4,
    disk_size: llm_disk_size_gb,
    max_price: 10,
    preemptible: false,
  };

  // LLM/guardrail jobs always use the minimum disk size; do not allow edits.
  // Guardrail jobs always use CPU Only and the default max price.
  const initial_values_normalized = normalize_resource_values(initial_values);

  // Validate initial values
  const initial_disk_value = initial_values_normalized.disk_size;
  const initial_disk_size_error =
    !(initial_disk_value >>> 0 === parseFloat(initial_disk_value)) ||
    initial_disk_value < effective_disk_min ||
    initial_disk_value > disk_max;

  const initial_job_name_error =
    initial_values_normalized.job_name === '' ||
    /[#$<>+%!`&*'"|{}?/\\:=@]/.test(initial_values_normalized.job_name);

  const initial_max_price_parsed = parseFloat(initial_values_normalized.max_price);
  const initial_max_price_error =
    isNaN(initial_max_price_parsed) ||
    initial_max_price_parsed < 0.05 ||
    initial_max_price_parsed > 1000;

  const [disk_size_error, setDiskSizeError] = useState(initial_disk_size_error);
  const [job_name_error, setJobNameError] = useState(initial_job_name_error);
  const [max_price_error, setMaxPriceError] = useState(initial_max_price_error);
  const [cpu_only, setCpuOnly] = useState(
    is_guardrail_job ||
      initial_values_normalized.gpu_types[0] === cpu_gpu_type_id,
  );
  const [values, setValues] = useState(initial_values_normalized);

  const has_notified_parent_ref = useRef(false);
  const is_values_effect_initial_ref = useRef(true);
  const initialValuesRef = useRef(initial_values_normalized);
  const onChangeRef = useRef(onChange);
  const last_synced_job_name_ref = useRef(initial_values_normalized.job_name);

  // Keep onChange ref up to date
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    if (user && user.selected_project) {
      dispatch(fetchProjectGpuClasses(user.selected_project));
    }
  }, [dispatch, user]);

  // Calculate error based on current values
  const calculateError = useCallback(
    (current_values) => {
      const disk_value_normalized = is_fixed_disk_job
        ? llm_disk_size_gb
        : current_values.disk_size;
      const current_job_name_error =
        current_values.job_name === '' ||
        /[#$<>+%!`&*'"|{}?/\\:=@]/.test(current_values.job_name);

      const current_disk_size_error =
        !(disk_value_normalized >>> 0 === parseFloat(disk_value_normalized)) ||
        disk_value_normalized < effective_disk_min ||
        disk_value_normalized > disk_max;

      const max_price_parsed = parseFloat(current_values.max_price);
      const current_max_price_error =
        isNaN(max_price_parsed) ||
        max_price_parsed < 0.05 ||
        max_price_parsed > 1000;

      return (
        current_disk_size_error ||
        current_job_name_error ||
        current_max_price_error ||
        current_values.gpu_types.length === 0 ||
        current_values.job_name === ''
      );
    },
    [effective_disk_min, disk_max, is_fixed_disk_job],
  );

  // Sync when parent initialValues update (e.g. copy job data loaded from source job)
  useEffect(() => {
    const job_name = initialValues?.job_name;
    if (!job_name || last_synced_job_name_ref.current === job_name) {
      return;
    }
    last_synced_job_name_ref.current = job_name;
    const normalized = normalize_resource_values(initialValues);
    setValues(normalized);
    if (is_guardrail_job) {
      setCpuOnly(true);
    }
    setJobNameError(
      job_name === '' || /[#$<>+%!`&*'"|{}?/\\:=@]/.test(job_name),
    );
  }, [initialValues, is_guardrail_job, normalize_resource_values]);

  // Initial mount effect - only call onChange once on mount
  useEffect(() => {
    if (has_notified_parent_ref.current) {
      return;
    }
    has_notified_parent_ref.current = true;
    const initial_payload = normalize_resource_values(initialValuesRef.current);
    const initial_error = calculateError(initial_payload);
    onChangeRef.current('resources', initial_payload, initial_error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoize gpu_types string to avoid complex expression in dependency array
  const gpu_types_string = useMemo(
    () => JSON.stringify(values.gpu_types),
    [values.gpu_types],
  );

  useEffect(() => {
    if (is_values_effect_initial_ref.current) {
      is_values_effect_initial_ref.current = false;
      return;
    }

    const values_normalized = normalize_resource_values(values);
    const max_price_parsed = parseFloat(values_normalized.max_price);
    const has_error = calculateError(values_normalized);

    onChangeRef.current(
      'resources',
      {
        ...values_normalized,
        max_price: max_price_parsed,
      },
      has_error,
    );
  }, [
    values,
    values.job_name,
    values.gpu_types.length,
    gpu_types_string,
    values.disk_size,
    values.max_price,
    values.gpu_count,
    values.cpu_count,
    calculateError,
    disk_min,
    disk_max,
    normalize_resource_values,
  ]);

  const handleChange = (e, { name, value }) => {
    switch (name) {
      case 'job_name':
        const job_name_error_new =
          value === '' || /[#$<>+%!`&*'"|{}?/\\:=@]/.test(value);
        setJobNameError(job_name_error_new);
        setValues((prevValues) => ({ ...prevValues, [name]: value }));
        break;
      case 'disk_size':
        if (is_fixed_disk_job) {
          setDiskSizeError(false);
          setValues((prevValues) => ({
            ...prevValues,
            disk_size: llm_disk_size_gb,
          }));
          break;
        }
        const disk_value = isNaN(parseInt(value)) ? '' : parseInt(value);
        const disk_size_error_new =
          !(disk_value >>> 0 === parseFloat(disk_value)) ||
          disk_value < disk_min ||
          disk_value > disk_max;
        setDiskSizeError(disk_size_error_new);
        setValues((prevValues) => {
          const new_values = { ...prevValues, [name]: disk_value };
          return new_values;
        });
        break;
      case 'max_price':
        const max_price_parsed = parseFloat(value);
        const max_price_error_new =
          isNaN(max_price_parsed) ||
          max_price_parsed < 0.05 ||
          max_price_parsed > 1000;
        setMaxPriceError(max_price_error_new);
        setValues((prevValues) => {
          const new_values = { ...prevValues, [name]: value };
          return new_values;
        });
        break;
      case 'gpu_count':
        setValues((prevValues) => {
          let min_cpu_count = 4;

          if (gpu_classes && gpu_classes.length > 0) {
            prevValues.gpu_types.forEach((gpu_type_id) => {
              const selected_gpu_class = gpu_classes.find(
                (gpu_class) => gpu_class.id === gpu_type_id,
              );
              if (selected_gpu_class && selected_gpu_class.cpus) {
                min_cpu_count = Math.max(
                  selected_gpu_class.cpus,
                  min_cpu_count,
                );
              }
            });
          }
          return {
            ...prevValues,
            gpu_count: value,
            cpu_count: min_cpu_count * value,
          };
        });
        break;
      case 'cpu_count':
        setValues((prevValues) => ({ ...prevValues, [name]: value }));
        break;
      default:
        setValues((prevValues) => ({ ...prevValues, [name]: value }));
    }
  };

  const handleGpuSelection = (gpu_type_id) => {
    setValues((prevValues) => {
      let gpu_types;
      if (typeof gpu_type_id === 'object') {
        // changing between cpu and gpu is disabled on edit form
        gpu_types = [...gpu_type_id];
      } else {
        if (gpu_type_id === cpu_gpu_type_id) {
          gpu_types = [cpu_gpu_type_id];
          setCpuOnly(true);
          return {
            ...prevValues,
            gpu_types,
            cpu_count: 4,
            gpu_count: 0,
          };
        } else if (prevValues.gpu_types[0] === cpu_gpu_type_id) {
          gpu_types = [gpu_type_id];
        } else {
          gpu_types = [...prevValues.gpu_types];
          if (prevValues.gpu_types.includes(gpu_type_id)) {
            _.remove(gpu_types, (gpu_type) => gpu_type === gpu_type_id);
          } else {
            gpu_types.push(gpu_type_id);
          }
        }
      }

      let min_cpu_count = 4,
        max_per_worker = 16;
      if (gpu_classes && gpu_classes.length > 0) {
        gpu_types.forEach((gpu_type_id) => {
          const selected_gpu_class = gpu_classes.find(
            (gpu_class) => gpu_class.id === gpu_type_id,
          );
          if (selected_gpu_class) {
            if (selected_gpu_class.cpus) {
              min_cpu_count = Math.max(selected_gpu_class.cpus, min_cpu_count);
            }
            if (selected_gpu_class.max_per_worker) {
              max_per_worker = Math.min(
                selected_gpu_class.max_per_worker,
                max_per_worker,
              );
            }
          }
        });
      }

      setCpuOnly(false);
      return {
        ...prevValues,
        gpu_types,
        cpu_count:
          min_cpu_count * (Math.min(prevValues.gpu_count, max_per_worker) || 1),
        gpu_count: Math.min(prevValues.gpu_count, max_per_worker) || 1,
      };
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid size={12}>
        <JobNameField
          value={values.job_name}
          onChange={handleChange}
          error={job_name_error}
          disabled={edit}
          required
        />
      </Grid>

      {!is_guardrail_job ? (
        <Grid size={12}>
          <GpuTypeField
            onClick={handleGpuSelection}
            gpu_types={values.gpu_types}
            cards={!edit && !small}
            edit={edit}
          />
        </Grid>
      ) : null}

      <Grid size={{ xs: 12, sm: 4 }}>
        {cpu_only ? (
          <FormControl fullWidth>
            <InputLabel id='cpu-count-label'>
              {type === 'training' ? 'CPUs Per Worker' : 'CPU Count'}
            </InputLabel>
            <Select
              labelId='cpu-count-label'
              id='cpu-count'
              name='cpu_count'
              value={values.cpu_count}
              label={type === 'training' ? 'CPUs Per Worker' : 'CPU Count'}
              onChange={(e) => {
                handleChange(e, {
                  name: 'cpu_count',
                  value: e.target.value,
                });
              }}
            >
              {[4, 8, 12, 16].map((count) => (
                <MenuItem key={count} value={count}>
                  {count}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <GpuCountField
            onChange={handleChange}
            gpu_types={values.gpu_types}
            gpu_count={values.gpu_count}
            type={type}
          />
        )}
      </Grid>

      {!is_guardrail_job ? (
        <Grid size={{ xs: 12, sm: 4 }}>
          <TextField
            name='max_price'
            label='Maximum Price per GPU (credits/hr)'
            type='number'
            value={values.max_price}
            onChange={(e) => {
              handleChange(e, {
                name: 'max_price',
                value: e.target.value,
              });
            }}
            disabled={edit}
            error={max_price_error}
            fullWidth
          />
          {max_price_error && (
            <FormHelperText error>
              Please enter decimal between 0.05 and 1000
            </FormHelperText>
          )}
        </Grid>
      ) : null}

      <Grid size={{ xs: 12, sm: 4 }}>
        {!is_fixed_disk_job ? (
          <>
            <TextField
              name='disk_size'
              label='Disk Size (GB)'
              type='number'
              value={values.disk_size}
              onChange={(e) => {
                handleChange(e, {
                  name: 'disk_size',
                  value: e.target.value,
                });
              }}
              disabled={edit}
              error={disk_size_error}
              fullWidth
            />
            {disk_size_error && (
              <FormHelperText error>
                Please enter an integer between {disk_min} and {disk_max}
              </FormHelperText>
            )}
          </>
        ) : null}
      </Grid>
    </Grid>
  );
}
